import { motion } from "framer-motion";

interface ProgressRingProps {
  progress: number; // 0-100
  size?: number;
  strokeWidth?: number;
  color?: string;
  bgColor?: string;
  showLabel?: boolean;
  label?: string;
  children?: React.ReactNode;
}

export const ProgressRing = ({
  progress,
  size = 120,
  strokeWidth = 8,
  color = "hsl(var(--gold))",
  bgColor = "hsl(var(--border))",
  showLabel = true,
  label,
  children,
}: ProgressRingProps) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (Math.min(progress, 100) / 100) * circumference;

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg
        width={size}
        height={size}
        className="transform -rotate-90"
      >
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={bgColor}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress circle */}
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="round"
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1, ease: "easeOut" }}
          style={{
            strokeDasharray: circumference,
          }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {children ? (
          children
        ) : showLabel ? (
          <>
            <span className="text-2xl font-display text-foreground">
              {Math.round(progress)}%
            </span>
            {label && (
              <span className="text-xs text-muted-foreground mt-0.5">
                {label}
              </span>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
};

interface MultiRingProps {
  rings: Array<{
    progress: number;
    color: string;
    label?: string;
  }>;
  size?: number;
  strokeWidth?: number;
  gap?: number;
}

export const MultiProgressRing = ({
  rings,
  size = 140,
  strokeWidth = 6,
  gap = 4,
}: MultiRingProps) => {
  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={size} height={size} className="transform -rotate-90">
        {rings.map((ring, index) => {
          const ringRadius = (size - strokeWidth) / 2 - (strokeWidth + gap) * index;
          const circumference = ringRadius * 2 * Math.PI;
          const offset = circumference - (Math.min(ring.progress, 100) / 100) * circumference;

          return (
            <g key={index}>
              {/* Background */}
              <circle
                cx={size / 2}
                cy={size / 2}
                r={ringRadius}
                stroke="hsl(var(--border))"
                strokeWidth={strokeWidth}
                fill="none"
                opacity={0.3}
              />
              {/* Progress */}
              <motion.circle
                cx={size / 2}
                cy={size / 2}
                r={ringRadius}
                stroke={ring.color}
                strokeWidth={strokeWidth}
                fill="none"
                strokeLinecap="round"
                initial={{ strokeDashoffset: circumference }}
                animate={{ strokeDashoffset: offset }}
                transition={{ duration: 1, ease: "easeOut", delay: index * 0.1 }}
                style={{
                  strokeDasharray: circumference,
                }}
              />
            </g>
          );
        })}
      </svg>
    </div>
  );
};